const TICK_MS = 1_000

function groupByTimezone(calendars) {
  const groups = []
  const index = new Map()

  calendars.forEach((row) => {
    if (!index.has(row.timezone_id)) {
      index.set(row.timezone_id, groups.length)
      groups.push({ timezone_id: row.timezone_id, rows: [] })
    }
    groups[index.get(row.timezone_id)].rows.push(row)
  })

  return groups
}

function localWeekdayAndMs(timezoneId, now) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezoneId,
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(now)

  const get = (type) => parts.find((p) => p.type === type)?.value ?? ''
  const weekday = get('weekday').toLowerCase()
  const hour = parseInt(get('hour'), 10) % 24
  const minute = parseInt(get('minute'), 10)
  const second = parseInt(get('second'), 10)
  const ms = ((hour * 60 + minute) * 60 + second) * 1000
  const pad = (n) => String(n).padStart(2, '0')

  return { weekday, ms, clock: `${pad(hour)}:${pad(minute)}:${pad(second)}` }
}

function localDateKey(timezoneId, now) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezoneId }).format(now)
}

function isRowOpen(row, weekday, ms, today) {
  if ((row.holidays || []).includes(today)) return false

  return (row.working_times || []).some((w) => w.weekday === weekday && ms >= w.start && ms < w.end)
}

const WEEKDAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const WEEKDAY_ABBR = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
}

function msToHHMM(ms) {
  const totalMinutes = Math.round(ms / 60_000) % (24 * 60)
  const hour = Math.floor(totalMinutes / 60)
  const minute = totalMinutes % 60

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function isFullDay(entry) {
  return entry.start === 0 && entry.end === 86_400_000
}

function formatDays(days) {
  const sorted = WEEKDAY_ORDER.filter((day) => days.includes(day))
  const indices = sorted.map((day) => WEEKDAY_ORDER.indexOf(day))
  const isConsecutiveRun = indices.length > 1 && indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1)

  return isConsecutiveRun
    ? `${WEEKDAY_ABBR[sorted[0]]}-${WEEKDAY_ABBR[sorted[sorted.length - 1]]}`
    : sorted.map((day) => WEEKDAY_ABBR[day]).join('/')
}

function formatSchedule(workingTimes) {
  const times = workingTimes || []

  if (times.length === 0) return 'Closed'
  if (WEEKDAY_ORDER.every((day) => times.some((w) => w.weekday === day && isFullDay(w)))) return '24/7'

  const byRange = new Map()
  times.forEach((w) => {
    const key = `${w.start}-${w.end}`
    if (!byRange.has(key)) byRange.set(key, { start: w.start, end: w.end, days: [] })
    byRange.get(key).days.push(w.weekday)
  })

  return [...byRange.values()]
    .map(({ start, end, days }) => `${formatDays(days)} ${msToHHMM(start)}-${msToHHMM(end)}`)
    .join(', ')
}

// Collapses same-worktype rows into one label with slash-joined priorities
// (e.g. "Incident P1/P2" instead of "Incident P1, Incident P2"). A row with no
// priority contributes no suffix; a worktype with only such rows renders bare.
function worktypeLabels(rows) {
  const byWorktype = new Map()

  rows.forEach((row) => {
    if (!byWorktype.has(row.worktype)) byWorktype.set(row.worktype, [])
    if (row.priority) byWorktype.get(row.worktype).push(row.priority)
  })

  return [...byWorktype.entries()].map(([worktype, priorities]) =>
    priorities.length ? `${worktype} ${priorities.join('/')}` : worktype,
  )
}

// Groups a timezone's rows by their formatted schedule so rows sharing identical hours
// (the common case) print as one "Mon-Fri 07:00-18:00: Incident P1/P2" line instead of
// repeating the schedule per row.
function scheduleLines(rows) {
  const bySchedule = new Map()

  rows.forEach((row) => {
    const schedule = formatSchedule(row.working_times)
    if (!bySchedule.has(schedule)) bySchedule.set(schedule, [])
    bySchedule.get(schedule).push(row)
  })

  return [...bySchedule.entries()].map(([schedule, groupedRows]) => ({
    schedule,
    names: worktypeLabels(groupedRows).join(', '),
  }))
}

export { formatSchedule, groupByTimezone, isRowOpen, localDateKey, localWeekdayAndMs, scheduleLines, TICK_MS }
